import type { ListingPage } from "@wags/catalog/listings/listing-page.contract.ts";

export const priceFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
});

const CURRENCY_CACHE = new Map<string, Intl.NumberFormat>();
const ISO_CURRENCY = /^[A-Z]{3}$/;

export type MoneyRange = NonNullable<ListingPage["listing"]["price"]>;

function currencyFormatter(currency: string): Intl.NumberFormat {
    const code = ISO_CURRENCY.test(currency) ? currency : "USD";
    let formatter = CURRENCY_CACHE.get(code);
    if (!formatter) {
        formatter = new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: code,
            maximumFractionDigits: 0,
        });
        CURRENCY_CACHE.set(code, formatter);
    }
    return formatter;
}

export function formatMoneyRange(
    range: MoneyRange | null | undefined,
): string | undefined {
    if (!range) return undefined;
    const format = currencyFormatter(range.currency);
    const { min, max } = range;
    if (min != null && max != null) {
        return min === max
            ? format.format(min)
            : `${format.format(min)}–${format.format(max)}`;
    }
    if (min != null) return `From ${format.format(min)}`;
    if (max != null) return `Up to ${format.format(max)}`;
    return undefined;
}

export function formatStartingPrice(
    range: MoneyRange | null | undefined,
): string | undefined {
    if (!range) return undefined;
    const format = currencyFormatter(range.currency);
    const { min, max } = range;
    if (min != null && max === min) return format.format(min);
    if (min != null) return `From ${format.format(min)}`;
    if (max != null) return `Up to ${format.format(max)}`;
    return undefined;
}

export function priceTransaction(
    range: MoneyRange | null | undefined,
    formatPrice: typeof formatMoneyRange = formatMoneyRange,
) {
    const price = formatPrice(range);
    const compactPrice = formatCompactMoneyRange(range);
    return price && compactPrice
        ? { kind: "price" as const, price, compactPrice }
        : null;
}

function formatCompactAmount(
    format: Intl.NumberFormat,
    amount: number,
    round: (thousands: number) => number,
) {
    return amount >= 10_000
        ? `${format.format(round(amount / 1000))}k`
        : format.format(amount);
}

export function formatCompactMoneyRange(
    range: MoneyRange | null | undefined,
): string | undefined {
    if (!range) return undefined;
    const format = currencyFormatter(range.currency);
    const { min, max } = range;
    if (min != null && max === min)
        return formatCompactAmount(format, min, Math.round);
    if (min != null) return `${formatCompactAmount(format, min, Math.floor)}+`;
    if (max != null)
        return `Up to ${formatCompactAmount(format, max, Math.ceil)}`;
    return undefined;
}

export const PRICE_DISPLAY_CLASS = "text-price text-doberman-black";
